<div
  class="app-shell"
  [ngClass]="{
    'theme-light': theme() === 'light',
    'theme-dark': theme() === 'dark',
    'app-shell--auth': isAuthRoute(),
  }"
>
  @if (!isAuthRoute() && !isMaintenanceRoute()) {
    <header class="app-header">
      <div class="header-bar">
        <div class="brand">
          <img class="brand__logo" src="/logo-normal.png" [attr.alt]="'app.logoAlt' | t" />
          <div class="brand__info">
            <h1>{{ 'app.title' | t }}</h1>
          </div>
        </div>
        <div class="header-actions">
          <button
            type="button"
            class="menu-toggle"
            (click)="toggleNavigation()"
            [class.menu-toggle--active]="isNavOpen()"
            [attr.aria-controls]="navigationId"
            [attr.aria-label]="isNavOpen() ? ('navigation.close' | t) : ('navigation.open' | t)"
            [attr.aria-expanded]="isNavOpen()"
          >
            <mat-icon class="menu-toggle__icon" icon="menu" aria-hidden="true"></mat-icon>
          </button>
        </div>
      </div>
      <nav [attr.id]="navigationId" class="app-nav" [class.app-nav--open]="isNavOpen()">
        <div class="app-nav__links">
          @for (link of links(); track link.path) {
            <a
              routerLinkActive="active"
              [routerLink]="link.path"
              (click)="closeNavigation()"
              class="app-nav__link"
            >
              <span class="app-nav__icon" aria-hidden="true">
                <mat-icon [icon]="link.icon"></mat-icon>
              </span>
              <span class="app-nav__label">{{ link.labelKey | t }}</span>
            </a>
          }
        </div>
        <button
          type="button"
          class="theme-toggle app-nav__theme-toggle"
          (click)="toggleTheme()"
          [attr.aria-label]="theme() === 'light' ? ('theme.ariaDark' | t) : ('theme.ariaLight' | t)"
        >
          @if (theme() === 'light') {
            <mat-icon
              class="theme-toggle__icon theme-toggle__icon--sun"
              icon="light_mode"
              aria-hidden="true"
            ></mat-icon>
          } @else {
            <mat-icon
              class="theme-toggle__icon theme-toggle__icon--moon"
              icon="dark_mode"
              aria-hidden="true"
            ></mat-icon>
          }
          <span class="theme-toggle__label">
            {{
              'theme.label'
                | t
                  : {
                      mode: theme() === 'light' ? ('theme.light' | t) : ('theme.dark' | t),
                    }
            }}
          </span>
        </button>
        <app-language-selector
          class="app-nav__language-selector"
          [languages]="supportedLanguages"
          [currentLanguage]="language()"
          (languageChange)="translationService.setLanguage($event)"
          labelTranslationKey="languageSelector.label"
          ariaLabelTranslationKey="languageSelector.ariaLabel"
        />
        <div class="auth-controls">
          @if (isAuthenticated()) {
            <button
              type="button"
              class="notification-button"
              (click)="goToNotifications()"
              [attr.aria-label]="
                unreadNotifications() > 0
                  ? ('navigation.notificationsNew' | t)
                  : ('navigation.notificationsDefault' | t)
              "
            >
              <span class="notification-button__icon" aria-hidden="true">
                <mat-icon
                  [icon]="unreadNotifications() > 0 ? 'notifications_active' : 'notifications'"
                ></mat-icon>
                @if (unreadNotifications() > 0) {
                  <span class="notification-button__badge">{{ unreadNotifications() }}</span>
                }
              </span>
              <span class="notification-button__label">{{ 'navigation.notifications' | t }}</span>
            </button>
            <div class="user-menu" [class.user-menu--open]="userMenuOpen()">
              <button
                type="button"
                class="user-menu__trigger"
                (click)="toggleUserMenu()"
                [attr.aria-expanded]="userMenuOpen()"
                aria-haspopup="true"
              >
                <div class="user-menu__label">
                  <span class="user-menu__name">{{ user()?.email }}</span>
                  <span class="user-menu__role">{{ user()?.role }}</span>
                </div>
                <span class="user-menu__chevron" aria-hidden="true">▾</span>
              </button>
              @if (userMenuOpen()) {
                <div class="user-menu__dropdown" role="menu">
                  <button
                    type="button"
                    class="user-menu__item"
                    role="menuitem"
                    (click)="goToAccount()"
                  >
                    {{ 'navigation.account' | t }}
                  </button>
                  <button
                    type="button"
                    class="user-menu__item"
                    role="menuitem"
                    (click)="goToAlerts()"
                  >
                    <span class="user-menu__icon" aria-hidden="true">
                      <mat-icon icon="notifications"></mat-icon>
                    </span>
                    {{ 'navigation.alerts' | t }}
                  </button>
                  <button
                    type="button"
                    class="user-menu__item"
                    role="menuitem"
                    (click)="goToFavorites()"
                  >
                    <span class="user-menu__icon" aria-hidden="true">
                      <mat-icon icon="star"></mat-icon>
                    </span>
                    {{ 'navigation.favorites' | t }}
                  </button>
                  <button
                    type="button"
                    class="user-menu__item user-menu__item--danger"
                    role="menuitem"
                    (click)="logout()"
                  >
                    {{ 'navigation.logout' | t }}
                  </button>
                </div>
              }
            </div>
          } @else {
            <a
              routerLink="/auth/login"
              routerLinkActive="active"
              (click)="closeNavigation()"
              class="link-button"
              >{{ 'navigation.login' | t }}</a
            >
          }
        </div>
      </nav>
      @if (isNavOpen()) {
        <div class="nav-backdrop" (click)="closeNavigation()" aria-hidden="true"></div>
      }
    </header>
  }

  @if (isAuthRoute() && !isMaintenanceRoute()) {
    <div class="auth-language-selector">
      <app-language-selector
        [languages]="supportedLanguages"
        [currentLanguage]="language()"
        (languageChange)="translationService.setLanguage($event)"
        labelTranslationKey="languageSelector.label"
        ariaLabelTranslationKey="languageSelector.ariaLabel"
      />
    </div>
  }

  @if (isLoading()) {
    <div class="loading-bar"></div>
  }

  <main class="app-main" [class.app-main--auth]="isAuthRoute() && !isMaintenanceRoute()">
    <router-outlet></router-outlet>
  </main>

  @if (!isAuthRoute() && !isMaintenanceRoute()) {
    <footer class="app-footer">
      <div class="footer-content">
        <span class="footer-item">{{ 'app.version' | t: { version: appVersion } }}</span>
        <span class="footer-item">{{
          'footer.copyright' | t: { year: currentYear, owner: appOwner() }
        }}</span>
        <span class="footer-item">{{
          'app.environment' | t: { environment: environmentLabel() }
        }}</span>
        <a routerLink="/terminos" class="footer-item footer-link">{{ 'footer.terms' | t }}</a>
        <a routerLink="/privacidad" class="footer-item footer-link">{{ 'footer.privacy' | t }}</a>
        <a routerLink="/aviso-legal" class="footer-item footer-link">{{ 'footer.legal' | t }}</a>
      </div>
    </footer>
  }

  <app-toast-container></app-toast-container>
</div>
